// Tournaments → Settings (contracts/tournaments.md §4, §9): the cups.* config keys. Whether cups run at all
// (cups.enabled) and whether players see them (cups.hidden; the app's features.cups is enabled and not hidden),
// the claim window, the countries that can't enter or can't win money prizes, and the Weekly Cup template
// (cups.weekly) the cups job creates the next two Weekly Cups from. Config writes are admin (PATCH /config, all or
// nothing); everyone else reads.

import { useState } from "react";
import { Link } from "react-router";
import type { Locale } from "../../api/messaging";
import { useConfig, usePatchConfig } from "../../api/queries";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, ErrorState, Field, Loading, PageHeader, Select, TextInput } from "../../components/ui";
import {
  type Bracket,
  bracketProblems,
  CUP_LIMITS,
  type EditPrize,
  editPrize,
  MAX_TEMPLATE_PRIZES,
  nextOpening,
  prizeOut,
  prizeProblems,
  readCupsConfig,
  REWARD_WORDING_HINT,
  SET_SIZE,
  WEEKDAY_NAME,
  WEEKDAYS,
  WeeklyTemplate,
} from "../../lib/cups";
import { toLocalized } from "../../lib/messaging";
import { ContentEditor, type ContentState, type FieldSpec, SwitchRow, textState } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import l from "../learn/learn.module.css";
import { BracketsEditor, CheckRow, CountriesField, PrizesEditor, SchedulePreview } from "./components";
import c from "./cups.module.css";

const TEXT_FIELDS = ["title", "subtitle", "prizeHeadline", "rules"] as const;
type TextKey = (typeof TEXT_FIELDS)[number];
const TEXT_SPECS: readonly FieldSpec<TextKey>[] = [
  { key: "title", label: "Title", limit: CUP_LIMITS.title, placeholder: "Weekly Cup #{number}", hint: "{number} becomes the cup's number" },
  { key: "subtitle", label: "Subtitle", limit: CUP_LIMITS.subtitle, rows: 2 },
  { key: "prizeHeadline", label: "Rewards line (shown in the app)", limit: CUP_LIMITS.prizeHeadline, hint: `on the cup card. ${REWARD_WORDING_HINT}` },
  { key: "rules", label: "Cup rules · optional", limit: CUP_LIMITS.rules, rows: 4, hint: REWARD_WORDING_HINT },
];
export function CupSettingsPage() {
  const config = useConfig();
  const canEdit = useCan("config.edit");
  const { admin } = useAuth();
  return (
    <>
      <PageHeader
        title="Tournament settings"
        meta={
          canEdit ? (
            <span>
              The cups.* config keys. <Link to="/tournaments">← Cups</Link>
            </span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · settings need the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      {config.isPending ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : config.isError ? (
        <ErrorState error={config.error} onRetry={() => void config.refetch()} />
      ) : (
        <Settings saved={readCupsConfig(config.data.values)} canEdit={canEdit} />
      )}
    </>
  );
}

function Settings({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  return (
    <div className={c.settingsGrid}>
      <GeneralCard saved={saved} canEdit={canEdit} />
      <WeeklyCard key={JSON.stringify(saved.weekly)} saved={saved.weekly} canEdit={canEdit} />
    </div>
  );
}

type Saved = ReturnType<typeof readCupsConfig>;

function GeneralCard({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  const set = usePatchConfig();
  const [enabled, setEnabled] = useState(saved.enabled);
  const [hidden, setHidden] = useState(saved.hidden);
  const [claimDays, setClaimDays] = useState(String(saved.claimDays));
  const [excluded, setExcluded] = useState(saved.excludedCountries);
  const [prizeExcluded, setPrizeExcluded] = useState(saved.prizeExcludedCountries);
  const [confirming, setConfirming] = useState(false);
  const days = Number(claimDays);
  const problem = !Number.isInteger(days) || days < 1 || days > 90 ? "The claim window is 1 to 90 days." : null;
  const writes: { key: string; label: string; from: string; to: string; value: unknown }[] = [];
  if (enabled !== saved.enabled) writes.push({ key: "cups.enabled", label: "Tournaments on", from: saved.enabled ? "on" : "off", to: enabled ? "on" : "off", value: enabled });
  if (hidden !== saved.hidden) writes.push({ key: "cups.hidden", label: "Hide tournaments in the app", from: saved.hidden ? "hidden" : "shown", to: hidden ? "hidden" : "shown", value: hidden });
  // What the app reads as features.cups once these values are saved.
  const playersSee = enabled && !hidden;
  if (days !== saved.claimDays) writes.push({ key: "cups.claimDays", label: "Claim window", from: `${saved.claimDays} d`, to: `${days} d`, value: days });
  if (excluded.join() !== saved.excludedCountries.join())
    writes.push({ key: "cups.excludedCountries", label: "Can't enter", from: saved.excludedCountries.join(", ") || "none", to: excluded.join(", ") || "none", value: excluded });
  if (prizeExcluded.join() !== saved.prizeExcludedCountries.join())
    writes.push({ key: "cups.prizeExcludedCountries", label: "Can't win money prizes", from: saved.prizeExcludedCountries.join(", ") || "none", to: prizeExcluded.join(", ") || "none", value: prizeExcluded });

  const save = () => set.mutate(Object.fromEntries(writes.map((w) => [w.key, w.value])) as never, { onSuccess: () => setConfirming(false) });

  return (
    <Card title="Tournaments" actions={<Chip tone={saved.playersSee ? "success" : "muted"}>{saved.playersSee ? "players see them" : saved.enabled ? "hidden in the app" : "off for players"}</Chip>}>
      <fieldset className={l.fieldset} disabled={!canEdit}>
        <div className={c.cardBody} data-testid="cups-general">
          <SwitchRow
            title="Tournaments on"
            hint="cups.enabled. Off: no new Weekly Cups and the app shows no tournaments; cups already made still run. To keep cups running but out of sight, hide them below."
            checked={enabled}
            onChange={setEnabled}
            label="Tournaments on"
            testId="cups-enabled"
          />
          <CheckRow
            title="Hide tournaments in the app"
            hint="cups.hidden. Players see no tournaments (no cards, sections, settings, links or pushes) while cups keep running: ranking, results, rewards and new Weekly Cups. Untick it to show them again."
            checked={hidden}
            onChange={setHidden}
            testId="cups-hidden"
          />
          <p className={c.effective} data-testid="players-see" data-value={playersSee ? "yes" : "no"}>
            Players see tournaments: <strong>{playersSee ? "yes" : "no"}</strong>
            {playersSee ? null : <span>{!enabled ? " · tournaments are off" : " · hidden in the app"}</span>}
            {playersSee !== saved.playersSee ? <span className={m.required}> · once saved</span> : null}
          </p>
          <Field label="Claim window (days)" hint={problem ? <span className={m.required}>{problem}</span> : "After the results are confirmed; unclaimed money prizes are then forfeited."}>
            <TextInput type="number" min={1} max={90} value={claimDays} onChange={(e) => setClaimDays(e.target.value)} style={{ width: 120 }} data-testid="claim-days" />
          </Field>
          <CountriesField
            label="Can't enter"
            hint="Storefront countries where cups aren't offered at all (403 country_excluded)."
            value={excluded}
            onChange={setExcluded}
            testId="excluded-countries"
          />
          <CountriesField
            label="Can't win money prizes"
            hint="Players there can enter and win XP, badges and Pro days, but not gift cards or other money prizes."
            value={prizeExcluded}
            onChange={setPrizeExcluded}
            testId="prize-excluded-countries"
          />
          {canEdit ? (
            <div className={c.rowActions}>
              <Button variant="primary" disabled={!writes.length || problem !== null} onClick={() => setConfirming(true)} data-testid="general-save">
                Save…
              </Button>
              {writes.length ? (
                <span className={styles.faint}>
                  {writes.length} unsaved {writes.length === 1 ? "change" : "changes"}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </fieldset>
      <ConfirmDialog
        open={confirming}
        title="Save tournament settings?"
        confirmLabel="Save"
        busy={set.isPending}
        error={set.error}
        onCancel={() => setConfirming(false)}
        onConfirm={save}
      >
        <ul className={c.prizeList} data-testid="general-changes">
          {writes.map((w) => (
            <li key={w.key}>
              <span>{w.label}</span>
              <span className={c.mono}>
                {w.from} → {w.to}
              </span>
            </li>
          ))}
        </ul>
        {playersSee !== saved.playersSee || (hidden && !saved.hidden) ? (
          <p className={styles.footNote} data-testid="players-see-change">
            {playersSee !== saved.playersSee
              ? playersSee
                ? "Players see tournaments on their next config fetch; the next cup's opening push goes out as scheduled. "
                : "The app hides every tournament surface on its next config fetch. "
              : ""}
            {hidden && !saved.hidden ? "While hidden, no cup pushes go out and cups keep running." : ""}
          </p>
        ) : null}
      </ConfirmDialog>
    </Card>
  );
}

function WeeklyCard({ saved, canEdit }: { saved: WeeklyTemplate; canEdit: boolean }) {
  const set = usePatchConfig();
  const [enabled, setEnabled] = useState(saved.enabled);
  const [weekday, setWeekday] = useState(saved.weekday);
  const [time, setTime] = useState(saved.time);
  const [hours, setHours] = useState(String(saved.durationHours));
  const [setSize, setSetSize] = useState(String(saved.setSize));
  const [brackets, setBrackets] = useState<Bracket[]>(saved.brackets);
  const [prizes, setPrizes] = useState<EditPrize[]>(() => saved.prizes.map(editPrize));
  const [text, setText] = useState<ContentState<TextKey>>(() => textState(TEXT_FIELDS, saved.content));
  const [locale, setLocale] = useState<Locale>("en");
  const [confirming, setConfirming] = useState(false);

  const value = {
    enabled,
    weekday,
    time,
    durationHours: Number(hours),
    setSize: Number(setSize),
    brackets: brackets.map((b) => ({ id: b.id, ...(b.name?.trim() ? { name: b.name.trim() } : {}), minLevel: b.minLevel, maxLevel: b.maxLevel ?? null, pool: b.pool })),
    prizes: prizes.map(prizeOut),
    content: toLocalized(text.draft, TEXT_FIELDS).content,
  };
  const parsed = WeeklyTemplate.safeParse(value);
  const problems = [
    ...(!text.draft.en.title.trim() ? ["Write the English title."] : []),
    ...(!/^([01]\d|2[0-3]):[0-5]\d$/.test(time) ? ["The opening time is HH:MM (UTC)."] : []),
    ...(!Number.isInteger(value.durationHours) || value.durationHours < 1 || value.durationHours > 168 ? ["A cup runs 1 to 168 hours."] : []),
    ...(!Number.isInteger(value.setSize) || value.setSize < SET_SIZE.min || value.setSize > SET_SIZE.max ? [`The set is ${SET_SIZE.min} to ${SET_SIZE.max} puzzles.`] : []),
    ...bracketProblems(brackets),
    ...prizeProblems(prizes, brackets, MAX_TEMPLATE_PRIZES),
  ];
  const ready = problems.length === 0 && parsed.success;
  const [baseline] = useState(() => JSON.stringify(value));
  const dirty = JSON.stringify(value) !== baseline;
  const next = nextOpening(weekday, time);
  const closes = next && Number(hours) > 0 ? new Date(Date.parse(next) + Number(hours) * 3_600_000).toISOString() : null;

  return (
    <Card title="Weekly Cup template" actions={<Chip tone={saved.enabled ? "success" : "muted"}>{saved.enabled ? "creating cups" : "paused"}</Chip>}>
      <fieldset className={l.fieldset} disabled={!canEdit}>
        <div className={c.cardBody} data-testid="weekly-template">
          <SwitchRow
            title="Create Weekly Cups"
            hint="The cups job keeps the next 2 Weekly Cups created from this template. Off: no new ones; already-created cups still run."
            checked={enabled}
            onChange={setEnabled}
            label="Create Weekly Cups"
            testId="weekly-enabled"
          />
          <div className={c.inline}>
            <Field label="Opens on">
              <Select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} data-testid="weekly-weekday">
                {WEEKDAYS.map((d) => (
                  <option key={d} value={d}>
                    {WEEKDAY_NAME[d]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="At (UTC)">
              <TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="weekly-time" />
            </Field>
            <Field label="Runs (hours)">
              <TextInput type="number" min={1} max={168} value={hours} onChange={(e) => setHours(e.target.value)} data-testid="weekly-hours" />
            </Field>
            <Field label="Puzzles">
              <TextInput type="number" min={SET_SIZE.min} max={SET_SIZE.max} value={setSize} onChange={(e) => setSetSize(e.target.value)} data-testid="weekly-set-size" />
            </Field>
          </div>
          {next && closes ? (
            <>
              <span className={styles.faint}>The next window this template would give, in a few time zones:</span>
              <SchedulePreview opensAt={next} closesAt={closes} />
            </>
          ) : null}
          <span className="monoLabel">Brackets</span>
          <BracketsEditor value={brackets} onChange={setBrackets} />
          <span className="monoLabel">Prizes</span>
          <PrizesEditor value={prizes} onChange={setPrizes} brackets={brackets} locale={locale} onLocale={setLocale} max={MAX_TEMPLATE_PRIZES} />
          <span className="monoLabel">Text</span>
          <ContentEditor value={text} onChange={setText} fields={TEXT_SPECS} englishRequired={["title"]} locale={locale} onLocale={setLocale} />
          {problems.length ? (
            <ul className={c.problems} data-testid="weekly-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          {canEdit ? (
            <div className={c.rowActions}>
              <Button variant="primary" disabled={!ready || !dirty} onClick={() => setConfirming(true)} data-testid="weekly-save">
                Save template…
              </Button>
              {dirty ? <span className={styles.faint}>unsaved changes</span> : null}
            </div>
          ) : null}
        </div>
      </fieldset>
      <ConfirmDialog
        open={confirming}
        title="Save the Weekly Cup template?"
        confirmLabel="Save"
        busy={set.isPending}
        error={set.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => parsed.success && set.mutate({ "cups.weekly": parsed.data as never }, { onSuccess: () => setConfirming(false) })}
      >
        <p className={styles.prose}>
          Writes <code>cups.weekly</code>. It applies to Weekly Cups created from now on: the next two already exist, so edit those one by one in <Link to="/tournaments">Cups</Link> if they should change too.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
